import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import Avatar from '@mui/material/Avatar';
import Backdrop from '@mui/material/Backdrop';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { LoaderArgs, Session } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useFetcher, useFetchers, useLoaderData, useLocation, useSearchParams } from '@remix-run/react';
import _ from 'lodash';
import type { Dispatch, KeyboardEventHandler, SetStateAction } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'remix-image';
import { Search } from '~/components/Navbar';
import ShopCategories, { CategoryAccordion } from '~/components/Shop/ShopCategories';
import {
  EmptyState,
  FiltersHeader,
  PriceRange,
  PrintMethods,
  PrintMethodsFilter,
  ProductionTime,
  ShippingFilter,
  getUpdatedFiltersSearch,
} from '~/components/Shop/common';
import { getLdFlag } from '~/components/launchdarkly.server';
import CircularProgress from '~/components/shared/CircularProgress';
import DialogMessage from '~/components/shared/DialogMessage';
import Pagination from '~/components/shared/Pagination';
import PriceBreakdown from '~/components/shared/PriceBreakdown';
import ProductCardWithAdd, { PackTemplateCardWithAdd } from '~/components/shared/ProductCardWithAdd';
import ProductModal from '~/components/shared/ProductModal';
import SkipPackBuilderButton, { SkipPackBuilderButtonMobile } from '~/components/shared/SkipPackBuilderButton';
import { SortBy } from '~/components/shared/SortBy';
import { MuiRemixLink } from '~/components/shared/links';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { Pack } from '~/routes/build-pack/create';
import { STEPS, firstCustomPackCategory, firstStarterPackCategory } from '~/routes/guided-pack-builder';
import type { Category, PriceEntry, Product } from '~/services/api.server';
import { scApi } from '~/services/api.server';
import { getUpdatedAuthentication } from '~/services/auth.server';
import { commitSession, getSession } from '~/services/session.server';
import {
  days,
  getEmptyArray,
  getFormattedPrice,
  isErrorResponse,
  isPackProduct,
  isPendingDesign,
  typedBoolean,
} from '~/utils/common';
import { ellipsisStyle, scrollbarStyle } from '~/utils/common-styles';
import { ProductTypes } from '~/utils/constants';
import { findSelectedCategory } from '~/utils/guided-pack-builder';
import {
  CATEGORIES_PARAM,
  EXPORTABLE_PARAM,
  MAX_AMOUNT_PARAM,
  MIN_AMOUNT_PARAM,
  PRODUCTION_TIME_PARAM,
  SEARCH_PARAM,
  TYPES_PARAM,
} from '~/utils/searchParams';
import {
  RemoveUnsetSearchParams,
  defaultSortValue,
  getBulkProductsWithLogo,
  getBulkSwagCategories,
  getCategories,
  getSwagPackCategories,
  productsSortOptions,
} from '~/utils/shop';
import {
  PackPackaging,
  PackProducts,
  getPricesRequestData,
  initialPackConfig,
  initialPackPrices,
  initialPrice,
  packActions,
  parsePricesTotal,
  useIsFetchingPrices,
} from '../build-pack/create';

export { action } from '~/routes/build-pack/create';

export async function loader({ request, params }: LoaderArgs) {
  const url = new URL(request.url);
  RemoveUnsetSearchParams(url);
  const session = await getSession(request);
  let pack = session.get('packBuilder') as Pack;
  const section = url.pathname.split('/')[2];
  const subSection = url.pathname.split('/')[3];
  let basePath = `/guided-pack-builder/${section}`;

  if (!pack) {
    pack = initialPackConfig;
    session.set('packBuilder', initialPackConfig);
    await getCookieHeader(session);
  }

  if (!pack.name) {
    pack.name = 'Default pack';
  }

  if (!subSection || subSection?.length === 0) {
    return redirect(`${basePath}/${Object.keys(STEPS[section])[0]}`);
  }

  // Get categories from steps dictionary and query all at once
  const categoriesToDisplay = STEPS[section][subSection]?.categories?.map((category) => category.slug);
  const categoriesToDisplayQuery = categoriesToDisplay?.join(',') || '';
  const categoryQuery = new URLSearchParams();
  categoryQuery.set('slug__in', categoriesToDisplayQuery);

  const [{ user }, categories] = await Promise.all([
    getUpdatedAuthentication(request),
    (() => {
      if (categoriesToDisplay?.length === 1 && categoriesToDisplay[0] === 'bulk-swag') return getBulkSwagCategories();
      if (subSection === 'select-a-pack') return getSwagPackCategories();
      return getCategories(categoryQuery);
    })(),
  ]);
  const accountId = session.get('accountId');

  const categorySlugsStr = params['*'] ?? '';
  const categorySlugs = categorySlugsStr.split('/').filter(Boolean);

  if (section === 'starter-pack') {
    basePath = basePath + `/${subSection}`;
  }

  const isPackaging = subSection === 'choose-packaging';

  if (!categories) {
    return redirect(basePath);
  }

  let categoriesList: Category[] | Category[][] = categories;
  // format categories to render properly in Subcategories component

  categoriesList = Array(
    categoriesList.reduce((prev, category) => {
      if (category.subcategories.length > 0) {
        return prev.concat(category.subcategories);
      } else {
        prev.push(category);
        return prev;
      }
    }, [] as Category[])
  );

  if (subSection !== 'choose-packaging') {
    categoriesList = [
      categoriesList[0].filter((category) => {
        return !['boxes', 'envelopes', 'packaging-fillers'].includes(category.slug);
      }),
    ];
  }

  let selectedCategory = findSelectedCategory(categories, categorySlugs.at(-1));

  if (selectedCategory) {
    url.searchParams.set(CATEGORIES_PARAM, selectedCategory.slug);
  } else {
    selectedCategory = categoriesList[0][0];

    url.searchParams.set(CATEGORIES_PARAM, selectedCategory?.slug);
    const redirectTo = new URL(request.url);
    const { pathname } = redirectTo;
    const { slug } = selectedCategory;
    redirectTo.pathname = `${pathname}${pathname.endsWith('/') ? '' : '/'}${slug}`;

    throw redirect(redirectTo.href, { headers: { 'Set-Cookie': await commitSession(session) } });
  }

  // get products or packs for /starter-pack section
  const isChoosePack = categorySlugs.some((slug) => slug === 'swag-packs');
  if (isChoosePack) {
    url.searchParams.append(TYPES_PARAM, ProductTypes.PACK_TEMPLATE.toString());
    url.searchParams.append(TYPES_PARAM, ProductTypes.CUSTOM_PACK.toString());
    url.searchParams.append(TYPES_PARAM, ProductTypes.CUSTOM_PACK_TEMPLATE.toString());
    if (categorySlugs.length > 1) {
      url.searchParams.set(CATEGORIES_PARAM, categorySlugs[categorySlugs.length - 1]);
    }
  }
  const isOtherProducts = categorySlugs.some((slug) => slug === 'bulk-swag');
  if (isOtherProducts) {
    url.searchParams.append(TYPES_PARAM, ProductTypes.SUPPLIER_PRODUCT.toString());
    url.searchParams.append(TYPES_PARAM, ProductTypes.CUSTOM_PRODUCT.toString());
    if (categorySlugs.length > 1) {
      url.searchParams.set(CATEGORIES_PARAM, categorySlugs[categorySlugs.length - 1]);
    }
  }

  const showAutomockupsFlag = await getLdFlag(user, 'show-auto-mockups-temp-05042023');

  const productsFilters = await scApi.getProductsAvailableFilters(user?.accessToken, accountId, {
    category__in: selectedCategory.id.toString(),
  });
  const cleanUrl = new URL(request.url);
  const updatedSearch = getUpdatedFiltersSearch(productsFilters, cleanUrl.searchParams);
  if (cleanUrl.search !== updatedSearch) {
    throw redirect(`${url.pathname}${updatedSearch}`, await getCookieHeader(session));
  }

  const products = await getBulkProductsWithLogo(
    showAutomockupsFlag,
    url.searchParams,
    user?.accessToken,
    accountId,
    productsFilters
  );

  const action = `/guided-pack-builder/${section}/${params['*']}${new URL(request.url).search}`;

  if (pack.products.length > 0 || pack.container || pack.filler) {
    const pricesRequest = getPricesRequestData(pack);
    const pricesResponse = await scApi.getPackBuilderPrices(pricesRequest);

    if (!isErrorResponse(pricesResponse)) {
      const updatedSession = await getSession(request);
      const updatedPack = updatedSession.get('packBuilder') as Pack;
      const prices = parsePricesTotal(pricesResponse, updatedPack.prices);
      const _products = [...updatedPack.products, updatedPack.container, updatedPack.filler].filter(typedBoolean);
      const withoutPrice = _.xor(
        _products.map((p) => p.id),
        Object.keys(prices.products).map((k) => parseInt(k.toString(), 10))
      );
      withoutPrice.forEach((id) => {
        prices.products[id] = prices.products[id] ?? initialPrice;
        Object.keys(prices.breakdowns).forEach((k) => {
          prices.breakdowns[k].products[id] = prices.breakdowns[k].products[id] ?? initialPrice;
        });
      });

      pricesResponse.products.forEach((p) => {
        updatedPack.items_attributes[p.product] = {
          product_id: p.product,
          attributes: Object.entries(p.attributes).map(([key, value]) => ({ name: key as string, value })),
        };
      });

      if (updatedPack.container) {
        const updatedContainer = pricesResponse?.products?.find((p) => p.product === updatedPack.container?.id);
        updatedPack.container.image = updatedContainer?.image;
        updatedPack.container.blank_image = updatedContainer?.blank_image;
      }

      if (updatedPack.filler) {
        const updatedFiller = pricesResponse?.products?.find((p) => p.product === updatedPack.filler?.id);
        updatedPack.filler.image = updatedFiller?.image;
        updatedPack.filler.blank_image = updatedFiller?.blank_image;
      }

      updatedPack.products.forEach((p) => {
        const updatedProduct = pricesResponse?.products?.find(
          (priceResponseProduct) => priceResponseProduct.product === p?.id
        );

        p.image = updatedProduct?.image;
        p.blank_image = updatedProduct?.blank_image;
      });

      return jsonWithCookieHeader(
        {
          isAuthenticated: user !== null,
          products,
          productsFilters,
          categories: categoriesList,
          selectedCategory,
          pack: { ...updatedPack, prices },
          isPackaging,
          action,
        },
        { ...updatedPack, prices },
        updatedSession
      );
    }
  } else if (pack.prices.pack_price.amount > 0) {
    return jsonWithCookieHeader(
      {
        isAuthenticated: user !== null,
        products,
        productsFilters,
        categories: categoriesList,
        selectedCategory,
        pack: { ...pack, prices: initialPackPrices },
        isPackaging,
        action,
      },
      { ...pack, prices: initialPackPrices },
      session
    );
  }
  return jsonWithCookieHeader(
    {
      isAuthenticated: user !== null,
      products,
      productsFilters,
      categories: categoriesList,
      selectedCategory,
      pack,
      isPackaging,
      action,
    },
    pack,
    session
  );
}

async function getCookieHeader(session: Session) {
  return { headers: { 'Set-Cookie': await commitSession(session) } };
}

async function jsonWithCookieHeader<T>(data: T, pack: Pack, session: Session) {
  session.set('packBuilder', pack);
  return json(data, await getCookieHeader(session));
}

const stepsDictionaryCustom = STEPS['custom-pack'];

const stepsDictionaryStarterPack = STEPS['starter-pack'];
const productsPerPage = 24;

export default function CreatePack() {
  const data = useLoaderData<typeof loader>();
  const fetcher = useFetcher();
  const submit = fetcher.submit;
  const [productInModal, setProductInModal] = React.useState<Product | null>(null);
  const { pathname } = useLocation();
  const section = pathname.split('/')[2];
  const currentLocation = pathname.split('/')[3];
  const stepsDictionary = (section === 'custom-pack' ? stepsDictionaryCustom : stepsDictionaryStarterPack) as any;
  const stepTitle = stepsDictionary[currentLocation];
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [showProductsGridBlur, setShowProductsGridBlur] = React.useState(true);
  const productsGridRef = React.useRef<HTMLDivElement | null>(null);

  const [query] = useSearchParams();
  const maxAmount = query.get(MAX_AMOUNT_PARAM) ?? data.productsFilters.price.max;
  const minAmount = query.get(MIN_AMOUNT_PARAM) ?? data.productsFilters.price.min;
  const productionTime = query.get(PRODUCTION_TIME_PARAM) ?? 0;

  const hasPriceFilter = Boolean(query.get(MIN_AMOUNT_PARAM) || query.get(MAX_AMOUNT_PARAM));
  const menuPriceText = hasPriceFilter
    ? `${getFormattedPrice((+minAmount || 0) / 100)} - ${getFormattedPrice((+maxAmount || 9000) / 100)}`
    : 'Price';
  const hasProductionTimeFilter = +productionTime !== 0;
  const menuProductionTimeText = hasProductionTimeFilter ? `${days(productionTime)}` : 'Production time';

  const shippingParam = query.get(EXPORTABLE_PARAM);
  const shippingParamText = !shippingParam ? 'Shipping' : shippingParam === 'true' ? 'Outside the US' : 'Within the US';

  const shouldShowPagination = data.products.results.length > 0 && data.products.count > productsPerPage;

  function handleCloseModal() {
    setProductInModal(null);
  }

  // reset products grid scroll to top when url changes
  React.useEffect(() => {
    if (productsGridRef?.current) {
      productsGridRef.current.scrollTop = 0;
    }
  }, [pathname]);

  // UIEvent<HTMLDivElement> or WheelEvent type don't work
  function handleProductsGridScroll(event: any) {
    const target = event.target;
    if (target.scrollHeight - target.scrollTop === target.clientHeight) {
      setShowProductsGridBlur(false);
    } else if (!showProductsGridBlur) {
      setShowProductsGridBlur(true);
    }
  }

  const debouncedNameChange = React.useMemo(
    () =>
      _.debounce((packName: string) => {
        submit({ _action: packActions.changeName, name: packName }, { method: 'post', action: '/build-pack/create' });
      }, 750),
    [submit]
  );

  const [openPackDialog, setOpenPackDialog] = React.useState<boolean>(
    Boolean(data.pack.container || data.pack?.filler || data.pack?.products?.length > 0)
  );

  const handleDeleteCurrentPack = () => {
    submit({ _action: packActions.deleteCurrentPack }, { method: 'post', action: '/build-pack/create' });
    setOpenPackDialog(false);
  };

  return (
    <>
      {(pathname.includes(firstCustomPackCategory) || pathname.includes(firstStarterPackCategory)) && (
        <DialogMessage
          openDialog={openPackDialog}
          setOpenDialog={setOpenPackDialog}
          dialogTitle="You are currently in the process of building a swag pack. What would you like to do next?"
          dialogContentText=""
          cancelButtonText="CONTINUE BUILDING"
          handleCancelButton={() => setOpenPackDialog(false)}
        >
          <Button
            variant="contained"
            sx={{
              boxShadow: 'none',
              fontSize: '13px',
              display: 'block',
              width: '100%',
              py: 1,
            }}
            onClick={handleDeleteCurrentPack}
          >
            CREATE NEW PACK
          </Button>
        </DialogMessage>
      )}

      <Box
        sx={{
          height: { xs: 'calc(100dvh - 112px)', md: 'calc(100vh - 72px)' },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '552px 1fr 110px' },
        }}
      >
        <Box
          sx={{
            padding: { xs: '8px 16px', md: 3 },
            position: 'relative',
            height: { xs: '40px', md: 'auto' },
            maxHeight: { md: 'calc(100vh - 72px)' },
            ...scrollbarStyle,
            borderBottom: { xs: `1px solid #EBEBEB`, md: '0px' },
          }}
        >
          {/* Desktop */}
          {!isMobile && (
            <>
              <TextField
                onChange={(e) => debouncedNameChange(e.target.value)}
                defaultValue={data?.pack?.name || 'Pack name'}
                size="small"
                fullWidth
                label="Pack name"
                inputProps={{ shrink: 'true' }}
                sx={{ mb: 3, display: 'flex' }}
              />
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                {stepTitle?.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                {stepTitle?.description}
              </Typography>
              <Box
                sx={{
                  display: { xs: 'none', md: 'grid' },
                  gridTemplateColumns: 'repeat(6, 1fr)',
                  gridTemplateRows: 'auto',
                  rowGap: 2,
                  columnGap: 1,
                  mb: 3,
                }}
              >
                <SearchDesktop />
                <MenuFilters
                  width={238}
                  btnText={menuPriceText}
                  menuText="Price"
                  containerSx={{ gridColumnStart: '1', gridColumnEnd: '3' }}
                  filter={<PriceRange minMax={data.productsFilters.price} />}
                  hasFilter={hasPriceFilter}
                />
                <MenuFilters
                  width={238}
                  btnText={menuProductionTimeText}
                  menuText="Production Time"
                  containerSx={{ gridColumnStart: '3', gridColumnEnd: '5' }}
                  filter={<ProductionTime minMax={data.productsFilters.production_time} />}
                  hasFilter={hasProductionTimeFilter}
                />
                <MenuFilters
                  btnText={shippingParamText}
                  menuText=""
                  containerSx={{ gridColumnStart: '5', gridColumnEnd: '7' }}
                  filter={<ShippingFilter />}
                  hasFilter={Boolean(shippingParam)}
                  noPadding
                />

                {/* <MoreFiltersDesktop /> */}
              </Box>
              {!!data.categories.length && <SubCategories />}
              <SkipPackBuilderButton iconInset={{ md: 'auto auto 32px 24px' }} />
              <PackDetailsDesktop />
              <Box
                position="absolute"
                bottom={0}
                left={0}
                width="50%"
                height="20%"
                borderRadius="50%"
                bgcolor="primary.main"
                zIndex={-1}
                sx={{ filter: 'blur(158.5px)', opacity: 0.25, display: { xs: 'none', md: 'block' } }}
              ></Box>
            </>
          )}

          {isMobile && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'row',
                justifyContent: 'space-between',
                // SortBy Custom Styles
                '.MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '.MuiInputBase-root': {
                  height: '24px',
                  '.MuiSvgIcon-root': {
                    color: 'text.disabled',
                    right: 0,
                  },
                  '.MuiSelect-select': {
                    color: 'text.disabled',
                    paddingRight: '28px',
                    '.MuiTypography-body2': {
                      color: 'text.secondary',
                    },
                  },
                },
              }}
            >
              <MoreFiltersMobile />
              <SortBy options={productsSortOptions} defaultOption={defaultSortValue} />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: 'white',
            position: 'relative',
            p: { xs: '16px', md: 0 },
          }}
        >
          <Box
            onScroll={handleProductsGridScroll}
            ref={productsGridRef}
            sx={{
              height: '100dvh',
              maxHeight: { xs: 'calc(100dvh - 170px)', md: 'calc(100vh - 72px)' },
              overflow: 'auto',
              padding: { xs: '8px', md: '16px' },
              backgroundColor: '#F0F5F9',
              borderRadius: { xs: '8px', md: '0' },

              ...scrollbarStyle,
              '& > div:first-of-type': {
                justifyContent: { md: 'end' },
                mb: { md: 2 },
              },
            }}
          >
            {!isMobile && <PrintMethods />}
            <Box
              sx={{
                display: data.products.results.length > 0 ? 'grid' : 'flex',
                isolation: 'isolate',
                gap: { xs: 1.5, sm: 2, lg: 3.75 },
                justifyContent: 'center',
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                  lg: 'repeat(auto-fill, minmax(250px, 1fr))',
                },
              }}
            >
              {/* Conditional for pack or other product */}
              {data.products.results.length > 0 ? (
                data.products.results.map((product: Product) => {
                  if (isPendingDesign(product.design_status)) {
                    return null;
                  }
                  if (isPackProduct(product)) {
                    return (
                      <PackTemplateCardWithAdd
                        key={product.id}
                        product={product}
                        pack={data.pack as Pack}
                        setProductInModal={setProductInModal}
                        action={data.action}
                      />
                    );
                  }
                  return (
                    <ProductCardWithAdd
                      key={product.id}
                      product={product}
                      pack={data.pack as Pack}
                      setProductInModal={setProductInModal}
                      isPackaging={data.isPackaging}
                      action={data.action}
                    />
                  );
                })
              ) : (
                <EmptyState />
              )}
              <ProductModal
                product={productInModal}
                pack={data.pack as Pack}
                isPackaging={data.isPackaging}
                onClose={handleCloseModal}
              />
            </Box>
            {shouldShowPagination && (
              <Stack alignItems="center" my={5}>
                <Pagination count={data.products.count} itemsPerPage={productsPerPage} />
              </Stack>
            )}
          </Box>
          {!isMobile && showProductsGridBlur && (
            <Box
              position="absolute"
              bottom={0}
              left={0}
              width="100%"
              height="10%"
              bgcolor="white"
              zIndex={1}
              sx={{ display: { xs: 'none', md: 'block' }, filter: 'blur(50px)', pointerEvents: 'none' }}
            ></Box>
          )}
        </Box>
        <Box sx={{ borderLeft: '1px solid #EBEBEB', display: { xs: 'none', md: 'block' } }}>
          <PackSummary />
        </Box>
      </Box>
      {isMobile && <PackDetailsMobile />}

      {isMobile && <SkipPackBuilderButtonMobile iconInset={{ xs: 'auto auto 62px 8px' }} />}
    </>
  );
}

function SearchDesktop() {
  const { t } = useTranslation('root');
  const [query, setSearchParams] = useSearchParams();
  const search = query.get(SEARCH_PARAM) ?? '';
  const inputRef = React.useRef<HTMLInputElement>();

  const handleClearSearch = () => {
    const searchParams = new URLSearchParams(query);
    searchParams.delete(SEARCH_PARAM);
    setSearchParams(searchParams, { replace: true });
  };

  const submitForm = () => {
    const searchParams = new URLSearchParams(query);
    const searchValue = inputRef.current?.value;
    if (searchValue !== '' && searchValue !== undefined) {
      searchParams.delete(SEARCH_PARAM);
      searchParams.set(SEARCH_PARAM, searchValue);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleInputPressEnter = (e: KeyboardEventHandler<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      const searchParams = new URLSearchParams(query);
      const searchValue = inputRef.current?.value;
      if (searchValue !== '' && searchValue !== undefined) {
        searchParams.delete(SEARCH_PARAM);
        searchParams.set(SEARCH_PARAM, searchValue);
        setSearchParams(searchParams, { replace: true });
      }
    }
  };

  return (
    <div
      style={{
        width: '100%',
        gridColumnStart: '1',
        gridColumnEnd: '7',
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '8px',
      }}
    >
      <TextField
        sx={{
          gridColumnStart: '1',
          gridColumnEnd: '5',
          borderColor: 'primary.main !important',
          '.MuiInputBase-input': { color: 'text.secondary' },
        }}
        onKeyPress={handleInputPressEnter}
        inputRef={inputRef}
        key={search}
        size="small"
        name={SEARCH_PARAM}
        defaultValue={search}
        placeholder={t('search') + '...'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        variant="outlined"
      />
      <Button
        variant="contained"
        sx={{ gridColumnStart: '5', gridColumnEnd: '6', boxShadow: 'none' }}
        type="submit"
        onClick={submitForm}
      >
        SEARCH
      </Button>
      <Button variant="outlined" onClick={handleClearSearch} sx={{ gridColumnStart: '6', gridColumnEnd: '7' }}>
        CLEAR
      </Button>
    </div>
  );
}

function MenuFilters({
  btnText,
  menuText,
  filter,
  containerSx,
  hasFilter,
  width,
  noPadding = false,
}: {
  btnText: string;
  menuText: string;
  filter: JSX.Element;
  containerSx: Record<string, string>;
  hasFilter: boolean;
  width?: number;
  noPadding?: boolean;
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  return (
    <Box sx={containerSx}>
      <Button
        variant="outlined"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={
          open ? (
            <ArrowDropUpIcon sx={{ fontSize: '24px !important', color: 'primary.main' }} />
          ) : (
            <ArrowDropDownIcon sx={{ fontSize: '24px !important' }} />
          )
        }
        sx={{
          height: '40px',
          width: '100%',
          borderColor: open || hasFilter ? 'primary.main' : '#C4C4C4',
          color: open || hasFilter ? 'primary.main' : 'text.secondary',
          fontSize: '14px',
          fontWeight: 400,
          justifyContent: 'space-between',
          alignItems: 'center',
          p: '5px 8px !important',
          '&:hover': { backgroundColor: '#fff', color: 'text.primary' },
          '.MuiButton-endIcon': { marginRight: '-5px' },
        }}
      >
        {btnText}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ '.MuiPaper-root': { width: width ? width : anchorEl?.offsetWidth } }}
      >
        <Stack p={noPadding ? 0 : 2}>
          <Typography variant="subtitle1" sx={{ fontSize: '16px', color: 'text.secondary', fontWeight: 700, mb: 1 }}>
            {menuText}
          </Typography>
          <Stack
            sx={{
              width: '100%',
              '.MuiBox-root': { ml: 0 },
              '.MuiTypography-body2': {
                color: 'text.secondary',
                fontSize: '12px',
                mb: 1,
                ml: '12px',
              },
              '.MuiSlider-root': { width: '206px' },
            }}
          >
            {React.cloneElement(filter, { handleClose })}
          </Stack>
        </Stack>
      </Menu>
    </Box>
  );
}

function MoreFiltersDesktop() {
  const [openMoreFilters, setOpenMoreFilters] = React.useState(false);

  return (
    <Box sx={{ gridColumnStart: '5', gridColumnEnd: '7' }}>
      <Badge
        badgeContent={3}
        color="primary"
        sx={{
          width: '100%',
          height: '100%',
          '& .MuiBadge-badge': {
            right: 6,
            top: 5,
          },
        }}
      >
        <Button
          variant="outlined"
          sx={{ width: '100%', height: '100%', fontSize: '15px' }}
          startIcon={<FilterListIcon />}
          onClick={() => setOpenMoreFilters(true)}
        >
          MORE FILTERS
        </Button>
        <FiltersDrawer drawerState={[openMoreFilters, setOpenMoreFilters]} />
      </Badge>
    </Box>
  );
}

function MoreFiltersMobile() {
  const [openMoreFilters, setOpenMoreFilters] = React.useState(false);
  const [query] = useSearchParams();

  return (
    <>
      <Box
        sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'row', gap: '14px', cursor: 'pointer' }}
        onClick={() => setOpenMoreFilters(true)}
      >
        <Badge
          badgeContent={Object.keys(Object.fromEntries(query.entries())).length}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              right: 1,
              top: 5,
              width: '15px',
              minWidth: '15px',
              height: '15px',
              fontSize: '11px',
            },
          }}
        >
          <FilterListIcon color="primary" />
        </Badge>
        <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: '500' }}>
          Filter
        </Typography>
      </Box>
      <FiltersDrawer drawerState={[openMoreFilters, setOpenMoreFilters]} />
    </>
  );
}

function FiltersDrawer({ drawerState }: { drawerState: [boolean, Dispatch<SetStateAction<boolean>>] }) {
  //const [showMoreColors, setShowMoreColors] = React.useState(false);
  const [openMoreFilters, setOpenMoreFilters] = drawerState;
  const data = useLoaderData<typeof loader>();
  const { pathname } = useLocation();

  let splitPathname = pathname.split('/');
  const basePath = splitPathname.slice(0, 4).join('/');

  let categories = data.categories;

  categories = categories.map((c) => {
    return c.map((c2) => ({ ...c2, href: `${basePath}/${c2.slug}` }));
  });

  return (
    <Drawer anchor="left" open={openMoreFilters} onClose={() => setOpenMoreFilters(false)}>
      <Box sx={{ p: '24px 16px', maxWidth: { xs: '277px', md: '260px' }, position: 'relative' }}>
        <CloseIcon
          sx={{
            display: { md: 'none' },
            position: 'absolute',
            top: '16px',
            right: '16px',
            color: '#757575',
            cursor: 'pointer',
          }}
          onClick={() => setOpenMoreFilters(false)}
        />
        <Box
          sx={{
            display: { md: 'none' },
            mt: '40px',
            '& > div:first-of-type': {
              p: 0,
              mb: 1.5,
            },
            '& > form:first-of-type': {
              mb: 1.5,
            },
            '& > div:nth-of-type(2)': {
              justifyContent: 'space-between',
            },
          }}
        >
          <FiltersHeader />
          <Search alwaysOpen path={pathname} />
          <PrintMethodsFilter />
          <ShopCategories categories={categories} />
          <CategoryAccordion expanded title="Price Range">
            <PriceRange minMax={data.productsFilters.price} />
          </CategoryAccordion>
          <CategoryAccordion expanded title="Production Time">
            <ProductionTime minMax={data.productsFilters.production_time} />
          </CategoryAccordion>
        </Box>

        {/* TODO: Add following components when API features are available */}
        {/* <Accordion
          sx={{
            mb: '32px',
            boxShadow: 'none',
            '&.MuiAccordionSummary-expandIconWrapper': {
              transition: 'none',
              '&.Mui-expanded': {
                transform: 'none',
              },
            },
            '&:before': {
              display: 'none',
            },
            '.Mui-expanded': {
              mb: '4px',
            },
          }}
        >
          <AccordionSummary sx={{ p: '0' }} expandIcon={<CustomExpandIcon />}>
            <Typography>Color</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: '0' }}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" position="relative">
              {showMoreColors ? (
                <>
                  {colorsMock.map((color) => (
                    <Color key={color.color.name} {...color} />
                  ))}
                </>
              ) : (
                <>
                  {colorsMock.slice(0, 4).map((color) => (
                    <Color key={color.color.name} {...color} />
                  ))}
                  <AddCircleOutlineOutlinedIcon
                    onClick={() => setShowMoreColors(true)}
                    sx={{
                      position: 'absolute',
                      right: '0',
                      cursor: 'pointer',
                      color: 'primary.main',
                      fontSize: '30px',
                      stroke: '#ffffff',
                      strokeWidth: 1,
                    }}
                  />
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion> */}
        {/* <Accordion
          sx={{
            mb: '32px',
            boxShadow: 'none',
            '&.MuiAccordionSummary-expandIconWrapper': {
              transition: 'none',
              '&.Mui-expanded': {
                transform: 'none',
              },
            },
            '&:before': {
              display: 'none',
            },
            '.Mui-expanded': {
              mb: '4px',
            },
          }}
        >
          <AccordionSummary sx={{ p: '0' }} expandIcon={<CustomExpandIcon />}>
            <Typography>Drink Capacity</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: '0' }}>
            <Typography>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse malesuada lacus ex, sit amet blandit
              leo lobortis eget.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          sx={{
            mb: '32px',
            boxShadow: 'none',
            '&.MuiAccordionSummary-expandIconWrapper': {
              transition: 'none',
              '&.Mui-expanded': {
                transform: 'none',
              },
            },
            '&:before': {
              display: 'none',
            },
            '.Mui-expanded': {
              mb: '4px',
            },
          }}
        >
          <AccordionSummary sx={{ p: '0' }} expandIcon={<CustomExpandIcon />}>
            <Typography>Customization</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: '0' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxOutlinedIcon sx={{ color: 'text.disabled' }} />}
                    sx={{
                      color: 'text.disabled',
                      '&.Mui-checked': {
                        '&, & + .MuiFormControlLabel-label': {
                          color: 'text.primary',
                        },
                      },
                    }}
                  />
                }
                label="1 color decoration"
                sx={{ '.MuiTypography-body1': { fontSize: '14px', color: 'text.disabled' } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxOutlinedIcon sx={{ color: 'text.disabled' }} />}
                    sx={{
                      color: 'text.disabled',
                      '&.Mui-checked': {
                        '&, & + .MuiFormControlLabel-label': {
                          color: 'text.primary',
                        },
                      },
                    }}
                  />
                }
                label="2 color decoration"
                sx={{ '.MuiTypography-body1': { fontSize: '14px', color: 'text.disabled' } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxOutlinedIcon sx={{ color: 'text.disabled' }} />}
                    sx={{
                      color: 'text.disabled',
                      '&.Mui-checked': {
                        '&, & + .MuiFormControlLabel-label': {
                          color: 'text.primary',
                        },
                      },
                    }}
                  />
                }
                label="3 color decoration"
                sx={{ '.MuiTypography-body1': { fontSize: '14px', color: 'text.disabled' } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxOutlinedIcon sx={{ color: 'text.disabled' }} />}
                    sx={{
                      color: 'text.disabled',
                      '&.Mui-checked': {
                        '&, & + .MuiFormControlLabel-label': {
                          color: 'text.primary',
                        },
                      },
                    }}
                  />
                }
                label="4 color decoration"
                sx={{ '.MuiTypography-body1': { fontSize: '14px', color: 'text.disabled' } }}
              />
            </FormGroup>
          </AccordionDetails>
        </Accordion> */}
      </Box>
    </Drawer>
  );
}

function SubCategories() {
  const { categories } = useLoaderData<typeof loader>();
  let location = useLocation();
  let splitPathname = location.pathname.split('/');
  let pathname = splitPathname.length > 4 ? splitPathname.slice(0, 4).join('/') : splitPathname.join('/');

  const categoriesLevels: Category[][] = [[]];

  categories.forEach((cat) => {
    if (Array.isArray(cat)) {
      cat.forEach((_cat) => {
        getCategoriesLevels(_cat, 0);
      });
    }
  });

  function getCategoriesLevels(category: Category, level: number) {
    categoriesLevels[level].push(category);
    if (category.subcategories.length > 0) {
      //add a new array for next level
      if (categoriesLevels[level + 1] === undefined) {
        categoriesLevels.push([]);
      }
      category.subcategories.forEach((subcategory) =>
        getCategoriesLevels({ ...subcategory, slug: `${category.slug}/${subcategory.slug}` }, level + 1)
      );
    }
  }

  return (
    <Stack
      sx={{ maxHeight: 'calc(100vh - 480px) ', overflowX: 'hidden', ...scrollbarStyle }}
      divider={<Divider />}
      spacing={2}
    >
      {categoriesLevels.length > 0 &&
        categoriesLevels.map((c, indexCategories) => {
          //Hide subcategories that aren't required
          if (indexCategories + 4 > splitPathname.length) return null;
          return (
            <Box
              key={c.toString()}
              sx={{
                display: { xs: 'none', md: 'grid' },
                flexDirection: 'row',
                gap: '26px',
                gridTemplateColumns: 'repeat(5, 1fr)',
                maxWidth: 'calc(100% - 4px ) ',
              }}
            >
              {c.map((sc) => (
                <CategoryLink
                  show={indexCategories === 0}
                  key={sc.id}
                  subCategory={sc}
                  pathname={pathname}
                  currentLocationIndex={3 + indexCategories}
                />
              ))}
            </Box>
          );
        })}
    </Stack>
  );
}

function CategoryLink({
  subCategory,
  pathname,
  currentLocationIndex,
  show,
}: {
  subCategory: Category;
  pathname: string;
  currentLocationIndex: number;
  show: boolean;
}) {
  let location = useLocation();
  const currentLocation = location.pathname.split('/')[currentLocationIndex];
  const isSelected = location.pathname.split('/').at(currentLocationIndex + 1) === subCategory.slug.split('/').at(-1);
  const isSameCategory = subCategory.slug.includes(currentLocation);

  return show || isSameCategory ? (
    <MuiRemixLink
      key={subCategory.slug}
      href={`${pathname}/${subCategory.slug}${location.search}`}
      color="inherit"
      underline="none"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
          textAlign: 'center',
          color: isSelected ? 'primary.main' : 'text.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            height: '80px',
            width: '80px',
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : '#FAFAFA',
          }}
        >
          <img
            src={subCategory.icon || ''}
            alt={subCategory.name}
            width={40}
            height={40}
            style={{
              filter: isSelected
                ? 'invert(47%) sepia(87%) saturate(2532%) hue-rotate(161deg) brightness(85%) contrast(102%)'
                : 'inherit',
            }}
          />
        </Box>

        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: isSelected ? 'primary.main' : 'text.secondary',
          }}
        >
          {subCategory.name}
        </Typography>
      </Box>
    </MuiRemixLink>
  ) : null;
}

function PackDetailsMobile() {
  const [showPackDetails, setShowPackDetails] = React.useState(false);

  return (
    <Box sx={{ display: { xs: 'block', md: 'none' }, boxShadow: 3 }}>
      <Box
        sx={{
          cursor: 'pointer',
          borderRadius: '8px',
          height: '40px',
          boxShadow: 3,
          width: '100%',
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: { xs: showPackDetails ? 'flex-end' : 'space-between', md: 'center' },
          flexDirection: 'row',
          padding: '9px 16px',
        }}
      >
        <PackDetails showPackDetails={showPackDetails} setShowPackDetails={setShowPackDetails} />
      </Box>
    </Box>
  );
}

function PackDetailsDesktop() {
  const [showPackDetails, setShowPackDetails] = React.useState(false);

  return (
    <Box sx={{ position: 'absolute', inset: 'auto 16px 32px auto', display: { xs: 'none', md: 'block' } }}>
      <Box
        sx={{
          cursor: 'pointer',
          position: 'relative',
          borderRadius: '8px',
          height: '50px',
          boxShadow: 3,
          width: showPackDetails ? '50px' : 'auto',
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'row',
          padding: '14px 10px',
          maxWidth: '360px',
        }}
      >
        <PackDetails showPackDetails={showPackDetails} setShowPackDetails={setShowPackDetails} />
      </Box>
    </Box>
  );
}

function PackDetails({
  showPackDetails,
  setShowPackDetails,
}: {
  showPackDetails: boolean;
  setShowPackDetails: Dispatch<SetStateAction<boolean>>;
}) {
  const { pack } = useLoaderData<typeof loader>();
  const [isOpenPriceBreakDown, setIsOpenPriceBreakDown] = React.useState(true);
  const [quantity, setQuantity] = React.useState<number | string>(pack.quantity);
  const isFetchingPrice = useIsFetchingPrices();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const packHasItems = pack.container || pack.filler || pack.products.length > 0;

  const { submit } = useFetcher();

  function handleChangeQuantity(value: string) {
    const parsedQty = parseInt(value, 10);
    if ((parsedQty === 0 || parsedQty > 5000) && value !== '') return;

    setQuantity(value ? parsedQty : '');
    if (parsedQty) {
      submit(
        { _action: 'changeQuantity', quantity: parsedQty.toString() },
        { method: 'post', action: '/build-pack/create' }
      );
    }
  }

  const debouncedNameChange = React.useMemo(
    () =>
      _.debounce((packName: string) => {
        submit({ _action: packActions.changeName, name: packName }, { method: 'post', action: '/build-pack/create' });
      }, 750),
    [submit]
  );

  return (
    <>
      {!showPackDetails && (
        <>
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'flex' } }} />}
            spacing={1}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: { xs: '2px', md: '10px' } }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px', fontWeight: 500 }}>
                {isMobile ? 'Qty:' : 'Packs:'}
              </Typography>
              <TextField
                variant="outlined"
                value={quantity}
                onChange={(e) => handleChangeQuantity(e.target.value)}
                type="number"
                size="small"
                inputProps={{ min: 1, max: 5000 }}
                sx={{
                  maxWidth: '70px',
                  '.MuiInputBase-input': {
                    padding: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'text.primary',
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: { xs: '4px', sm: '10px' },
                ml: { xs: '4px !important', sm: '8px !important' },
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px', fontWeight: 500 }}>
                Total:
              </Typography>
              <Typography
                variant="body2"
                sx={[
                  {
                    color: 'text.primary',
                    fontSize: '14px',
                    fontWeight: 500,
                    maxWidth: { xs: 'calc(100vw - 270px)', md: '84px' },
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    mr: 1,
                  },
                  isFetchingPrice && { color: 'text.disabled' },
                ]}
              >
                {getFormattedPrice((pack.quantity * pack.prices.pack_prices.total.amount) / 100)}
              </Typography>
            </Box>
          </Stack>
        </>
      )}
      <IconButton
        id="iconButtonPack"
        onClick={showPackDetails ? () => setShowPackDetails(false) : () => setShowPackDetails(true)}
        sx={{ p: 0 }}
      >
        {!showPackDetails ? (
          <>
            {isMobile && (
              <Typography
                sx={{
                  color: 'primary.main',
                  fontSize: '14px',
                  fontWeight: 500,
                  mr: { xs: '4px', md: 1 },
                  display: { md: 'none' },
                }}
              >
                Pack details
              </Typography>
            )}
            <ExpandLessIcon sx={{ color: 'primary.main' }} />
          </>
        ) : (
          <ExpandMoreIcon sx={{ color: 'primary.main' }} />
        )}
      </IconButton>

      <Backdrop
        id="backdropId"
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'absolute',
          right: '0',
          bottom: { xs: '42px', md: '60px' },
          alignItems: 'end',
          display: showPackDetails ? 'block' : 'none',
        }}
        open={showPackDetails}
        invisible={!isMobile}
      >
        <Box
          sx={[
            {
              color: 'text.primary',
              width: { xs: '100vw', md: '510px' },
              backgroundColor: 'white',
              boxShadow: { xs: 0, md: 2 },
              borderRadius: { xs: '8px 8px 0px 0px', md: '8px' },
              borderBottom: { xs: '1px solid #EBEBEB', md: '0px' },
              padding: '16px',
              height: {
                xs: packHasItems ? 'calc(100dvh - 78px)' : isOpenPriceBreakDown ? '390px' : '325px',
                md: isOpenPriceBreakDown ? '305px' : '247px',
              },
              transition: !isOpenPriceBreakDown ? 'height 0.3s' : 'inherit',
              position: 'absolute',
              right: '0',
              bottom: { xs: '0', md: '8px' },
            },
            isMobile && { ...scrollbarStyle, maxHeight: { xs: 'calc(100dvh - 78px)', md: 'initial' } },
          ]}
        >
          <Box>
            <TextField
              onChange={(e) => debouncedNameChange(e.target.value)}
              defaultValue={pack?.name || 'Pack name'}
              size="small"
              fullWidth
              label="Pack name"
              inputProps={{ shrink: 'true' }}
              sx={{ mb: 2, display: { xs: 'flex', md: 'none' } }}
            />
            <PriceBreakdown
              isFetchingPrice={isFetchingPrice}
              breakdowns={pack.prices.breakdowns}
              open={isOpenPriceBreakDown}
              quantity={quantity}
              setOpen={setIsOpenPriceBreakDown}
              onChangeQuantity={handleChangeQuantity}
            />
            <PriceInfo
              pack={pack as Pack}
              quantity={quantity}
              setQuantity={setQuantity}
              onChangeQuantity={handleChangeQuantity}
            />
          </Box>
          {isMobile && packHasItems && (
            <Stack>
              <PackPackaging pack={pack as Pack} />
              <PackProducts pack={pack as Pack} />
            </Stack>
          )}
        </Box>
      </Backdrop>
    </>
  );
}

function PriceInfo({
  pack,
  quantity,
  onChangeQuantity,
}: {
  pack: Pack;
  quantity: number;
  onChangeQuantity: (value: string) => void;
}) {
  const decorationFetcher = useFetcher();
  const isFetchingPrice = useIsFetchingPrices();

  function handleDecorationColorQty(event: SelectChangeEvent) {
    decorationFetcher.submit(
      {
        _action: packActions.changePackDecorationColorQuantity,
        decorationQuantity: event.target.value,
      },
      { method: 'post', action: '/build-pack/create' }
    );
  }

  return (
    <Stack sx={{ py: 2, px: 1 }} spacing={1}>
      <Box sx={{ display: 'flex', direction: 'row', justifyContent: 'space-between' }}>
        <Typography variant="body2">Blank Products:</Typography>
        <Stack direction="row" alignItems="center">
          <CircularProgress show={isFetchingPrice} size={12} containerProps={{ mr: 1 }} />
          <Typography variant="body2" sx={[isFetchingPrice && { color: 'text.disabled' }]}>
            {getFormattedPrice(pack.prices.pack_prices.blank.amount / 100)}
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ display: 'flex', direction: 'row', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', direction: 'row', gap: '8px', alignItems: 'center' }}>
          <Typography variant="body2">Decoration with </Typography>
          <decorationFetcher.Form method="post">
            <Select
              value={pack.no_colors?.toString() || '1'}
              name="decorationQuantity"
              onChange={handleDecorationColorQty}
              sx={{
                height: '20px',
                width: '33px',
                '.MuiSelect-select': {
                  fontSize: '12px',
                  paddingLeft: '8px',
                },
                '.MuiOutlinedInput-notchedOutline': {
                  fontSize: '12px',
                },
                '.MuiSvgIcon-root': {
                  right: '-3px',
                  fontSize: '1.3rem',
                },
              }}
            >
              {getEmptyArray(10).map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </decorationFetcher.Form>
          <Typography variant="body2">{`color${Number(pack.no_colors) > 1 ? 's' : ''}:`}</Typography>
        </Box>
        <Stack direction="row" alignItems="center">
          <CircularProgress show={isFetchingPrice} size={12} containerProps={{ mr: 1 }} />
          <Typography variant="body2" sx={[isFetchingPrice && { color: 'text.disabled' }]}>
            {getFormattedPrice(pack.prices.pack_prices.decoration.amount / 100)}
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ display: 'flex', direction: 'row', justifyContent: 'space-between' }}>
        <Typography variant="body2">Price per pack</Typography>
        <Stack direction="row" alignItems="center">
          <CircularProgress show={isFetchingPrice} size={12} containerProps={{ mr: 1 }} />
          <Typography variant="body2" sx={[isFetchingPrice && { color: 'text.disabled' }]}>
            {getFormattedPrice(parseFloat(pack.prices.pack_price.display))}
          </Typography>
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', direction: 'row', justifyContent: 'space-between' }}>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            Packs:
          </Typography>
          <TextField
            variant="outlined"
            type="number"
            value={quantity}
            onChange={(e) => onChangeQuantity(e.target.value)}
            inputProps={{ min: 1, max: 5000 }}
            sx={{
              maxWidth: '70px',
              '.MuiInputBase-input': {
                padding: '6px 10px',
                fontSize: '12px',
                fontWeight: 500,
              },
            }}
          />
        </Stack>
        <Typography
          variant="body2"
          sx={[{ fontWeight: 500, color: 'text.primary' }, isFetchingPrice && { color: 'text.disabled' }]}
        >
          Total price: {getFormattedPrice((pack.quantity * pack.prices.pack_price.amount) / 100)}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
        Price based on 1 color and 1 decoration location.
      </Typography>
    </Stack>
  );
}

function PackSummary() {
  const { pack } = useLoaderData<typeof loader>();

  return (
    <Stack sx={{ textAlign: 'center' }}>
      <Stack sx={{ padding: '16px 8px', alignItems: 'center', borderBottom: '1px solid #E0E0E0' }}>
        <Typography variant="body2" sx={{ mb: '2px', color: 'text.primary', fontWeight: 500 }}>
          Pack
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            maxWidth: '74px',
            ...ellipsisStyle,
          }}
        >
          {getFormattedPrice(pack.prices.pack_price.amount / 100)}
        </Typography>
      </Stack>
      <PackSummaryCategory />
    </Stack>
  );
}

function PackSummaryCategory() {
  const { pack, action } = useLoaderData<typeof loader>();
  const [productInModal, setProductInModal] = React.useState<Product | null>(null);
  const fetchers = useFetchers();
  const productsBeingAdded = fetchers
    .filter((fetcher) => fetcher.submission?.formData.get('_action') === packActions.addProduct)
    .map((fetcher) => JSON.parse(fetcher.submission?.formData.get('product')?.toString() ?? '{}') as Product);

  const fillerFormData = fetchers
    .find((fetcher) => fetcher.submission?.formData.get('_action') === packActions.setFiller)
    ?.submission?.formData.get('product')
    ?.toString();
  const fillerBeingAdded = fillerFormData ? JSON.parse(fillerFormData) : null;

  const containerFormData = fetchers
    .find((fetcher) => fetcher.submission?.formData.get('_action') === packActions.setContainer)
    ?.submission?.formData.get('product')
    ?.toString();
  const containerBeingAdded = containerFormData ? JSON.parse(containerFormData) : null;

  const handleCloseModal = () => setProductInModal(null);

  return (
    <Box sx={{ maxHeight: 'calc(100vh - 200px)', ...scrollbarStyle }}>
      <Box
        sx={{
          backgroundColor: '#FAFAFA',
          p: '2px 0 4px 0',
          lineHeight: '10px',
          mb: !pack.container && !pack.filler && !containerBeingAdded && !fillerBeingAdded ? 11 : 0,
        }}
      >
        <Typography variant="caption" fontWeight={500} color="text.primary">
          Packaging
        </Typography>
      </Box>
      {pack.container && !containerBeingAdded && (
        <PackSummaryProduct
          product={pack.container}
          type="container"
          setProductInModal={setProductInModal}
          price={pack.prices.products[pack.container.id]}
          action={action}
        />
      )}
      {containerBeingAdded && (
        <PackSummaryProduct
          product={containerBeingAdded}
          quantity={pack.items_units_per_pack[containerBeingAdded.id]}
          type="product"
          setProductInModal={setProductInModal}
          disabledDelete
        />
      )}

      {pack.filler && !fillerBeingAdded && (
        <PackSummaryProduct
          product={pack.filler}
          type="filler"
          setProductInModal={setProductInModal}
          price={pack.prices.products[pack.filler.id]}
          action={action}
        />
      )}
      {fillerBeingAdded && (
        <PackSummaryProduct
          product={fillerBeingAdded}
          quantity={pack.items_units_per_pack[fillerBeingAdded.id]}
          type="product"
          setProductInModal={setProductInModal}
          disabledDelete
        />
      )}

      {(pack.products.length > 0 || productsBeingAdded.length > 0) && (
        <>
          <Box
            sx={{
              backgroundColor: '#FAFAFA',
              p: '2px 0 4px 0',
              lineHeight: '10px',
            }}
          >
            <Typography variant="caption" fontWeight={500} color="text.primary">
              Items
            </Typography>
          </Box>

          {pack.products.map((product) => (
            <PackSummaryProduct
              key={product.id}
              product={product}
              quantity={pack.items_units_per_pack[product.id]}
              type="product"
              setProductInModal={setProductInModal}
              price={pack.prices.products[product.id]}
              action={action}
            />
          ))}

          {productsBeingAdded.map((product) => (
            <PackSummaryProduct
              key={product.id}
              product={product}
              quantity={pack.items_units_per_pack[product.id]}
              type="product"
              setProductInModal={setProductInModal}
              disabledDelete
            />
          ))}
        </>
      )}
      {/* Here it doesn't matter the URL (isPackaging), the modal opens when interacting with the products on the right */}
      <ProductModal
        product={productInModal}
        onClose={handleCloseModal}
        pack={pack as Pack}
        isPackaging={pack.container?.id === productInModal?.id || pack.filler?.id === productInModal?.id}
      />
    </Box>
  );
}

function PackSummaryProduct({
  product,
  quantity,
  type,
  setProductInModal,
  price,
  disabledDelete,
  action,
}: {
  product: Product;
  quantity?: number;
  type: 'product' | 'filler' | 'container';
  setProductInModal: Dispatch<SetStateAction<Product | null>>;
  price?: PriceEntry;
  disabledDelete?: boolean;
  action?: string;
}) {
  const deleteFetcher = useFetcher();
  const changeQuantityFetcher = useFetcher();
  const [quantityState, setQuantityState] = React.useState<number | string>(quantity || 1);

  const debouncedQuantity = React.useMemo(
    () =>
      _.debounce((units: number | string) => {
        if (!units) return;
        changeQuantityFetcher.submit(
          { _action: packActions.changeUnitsPerPack, productId: product.id.toString(), unitsPerPack: units.toString() },
          { method: 'post', action }
        );
      }, 750),
    [product.id, action, changeQuantityFetcher]
  );
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const qty = e.currentTarget.value;
    const parsedQty = parseInt(qty, 10);
    if ((parsedQty === 0 || parsedQty > 5000) && qty !== '') return;
    setQuantityState(qty ? parsedQty : '');
    debouncedQuantity(qty ? parsedQty : '');
  };

  return (
    <Stack
      alignItems="center"
      sx={{ position: 'relative', p: '8px 15px 8px 15px', borderBottom: '1px solid #EBEBEB' }}
      data-product
    >
      <Avatar
        sx={{
          position: 'absolute',
          top: '1px',
          right: '8px',
          color: '#757575',
          backgroundColor: 'transparent',
          width: '20px',
          height: '20px',
          cursor: 'pointer',
        }}
      >
        <deleteFetcher.Form method="post" action={action}>
          <input type="hidden" name="productId" value={product.id} />
          <IconButton
            type="submit"
            name="_action"
            disabled={disabledDelete}
            value={
              type === 'container'
                ? packActions.setContainer
                : type === 'filler'
                ? packActions.setFiller
                : packActions.removeProduct
            }
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </deleteFetcher.Form>
      </Avatar>

      <Image
        src={product.image || product.blank_image || NoImageAvailable}
        alt="Product"
        width={56}
        height={56}
        onClick={() => setProductInModal(product)}
        responsive={[{ size: { height: 56, width: 56 } }]}
        dprVariants={[1, 3]}
        loading="lazy"
        options={{ fit: 'inside' }}
        style={{ cursor: 'pointer', width: '100%' }}
      />
      <Typography
        variant="caption"
        onClick={() => setProductInModal(product)}
        sx={{
          color: 'text.secondary',
          mt: 0.5,
          mb: 0.5,
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        {product.name}
      </Typography>
      {price?.amount ? (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: 'text.primary',
            mb: type === 'product' ? 0.5 : 0,
            ...ellipsisStyle,
          }}
        >
          {getFormattedPrice(price.amount / 100)}
        </Typography>
      ) : (
        <Stack alignItems="center" mb={type === 'product' ? 0.5 : 0}>
          <Skeleton width={60} sx={{ fontSize: 12 }} />
        </Stack>
      )}
      {type === 'product' && quantity !== undefined && (
        <Stack alignItems="center">
          <Typography variant="caption" color="#212121" mb={0.5}>
            Qty/pack
          </Typography>
          <TextField
            variant="outlined"
            type="number"
            value={quantityState}
            onChange={handleChange}
            inputProps={{ min: 1, max: 5000 }}
            sx={{
              maxWidth: '58px',
              '.MuiInputBase-input': {
                py: 1,
                px: 1.25,
                fontSize: 14,
                fontWeight: 400,
              },
            }}
          />
        </Stack>
      )}
    </Stack>
  );
}
